/**
 * Secure offline Premium gateway. Only Android uses the native vault.
 * The server's signed license, not localStorage or a browser timestamp,
 * authorizes access to the encrypted download.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';
import apiClient from './apiClient';

const vault = registerPlugin('OfflineVault');
const isAndroid = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

export function supportsPremiumOffline() {
  return isAndroid();
}

function userId() {
  return String(localStorage.getItem('concours_bf_current_user') || '');
}

export async function getOfflinePremiumStatus(id = userId()) {
  if (!isAndroid() || !id) return { available: false };
  try {
    return await vault.getStatus({ userId: String(id) });
  } catch {
    return { available: false };
  }
}

export async function downloadPremiumOffline() {
  if (!isAndroid()) throw new Error('Le téléchargement hors connexion est disponible uniquement sur Android.');
  const id = userId();
  if (!id) throw new Error('Connectez-vous avant de télécharger du contenu Premium.');
  const identity = await vault.getDeviceIdentity();
  const issued = await apiClient('/offline/package', {
    method: 'POST',
    timeoutMs: 90000,
    body: JSON.stringify({
      installationId: identity.installationId,
      devicePublicKeySpkiBase64: identity.devicePublicKeySpkiBase64,
      rsaOaepMgf1Digest: identity.rsaOaepMgf1Digest,
    }),
  });
  if (!issued?.license || !issued?.package) throw new Error('Paquet Premium incomplet.');
  const stored = await vault.storePackage({
    userId: id,
    license: issued.license,
    package: issued.package,
  });
  window.dispatchEvent(new Event('concoursbf:offline-package-ready'));
  return stored;
}

export async function readOfflinePremium() {
  if (!isAndroid() || !userId()) throw new Error('Mode hors connexion Premium indisponible.');
  // Do not cache plaintext at module scope or in persistent browser storage.
  const result = await vault.readPackage({ userId: userId() });
  return result.content;
}

export async function clearOfflinePremium() {
  if (!isAndroid()) return;
  await vault.clearPackage();
}

export function offlineAccessProfile(status) {
  if (!status?.available || !status.expiresAt) return null;
  return {
    tier: 'premium',
    premiumActive: true,
    premiumExpiresAt: status.expiresAt,
    entitlements: ['premium_content'],
    subscription: null,
    offline: true,
  };
}
